import React, {useMemo} from 'react';
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import type {HotspotExplorerProps, HotspotExplorerSortMode} from "../types";
import {
    buildCountyPath,
    buildCountryPath,
    buildHotspotExplorerSearch,
    buildHotspotDetailPath,
    getCachedHotspotExplorerStats,
    getCachedSortedHotspots,
    getCountryLabel,
    getHotspotExplorerHeroImage,
    getHotspotExplorerPage,
    getStateProvinceLabel,
} from "../helpers";
import {HotspotExplorerShell} from "../hotspot-explorer-shell";
import {usePageTitle} from "../../../helpers/usePageTitle";

export function StateHotspotsView({hotspotExplorerIndex}: HotspotExplorerProps) {
    const resolvedHotspotExplorerIndex = hotspotExplorerIndex!;
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {countryCode = '', stateProvince = ''} = useParams();
    const sortMode = (searchParams.get('sort') as HotspotExplorerSortMode) || 'visits';
    const currentPage = getHotspotExplorerPage(searchParams);
    const stateKey = `${countryCode}|${stateProvince}`;
    const hotspots = useMemo(() => resolvedHotspotExplorerIndex.hotspotsByStateKey.get(stateKey) ?? [], [resolvedHotspotExplorerIndex, stateKey]);
    const regionBounds = hotspots[0]?.regionData?.subnational1?.bounds ?? undefined;
    const stateLabel = hotspots[0]?.stateLabel || getStateProvinceLabel(stateProvince);
    const visibleHotspots = useMemo(
        () => getCachedSortedHotspots(hotspots, sortMode),
        [hotspots, sortMode]
    );
    const countyGroups = resolvedHotspotExplorerIndex.countyGroupsByStateKey.get(stateKey) ?? [];
    const countryLabel = hotspots[0]?.countryLabel || getCountryLabel(countryCode);

    usePageTitle(stateLabel, countryLabel, 'Hotspot Explorer');

    const setSortMode = (nextSortMode: HotspotExplorerSortMode) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('sort', nextSortMode);
        setSearchParams(nextParams, {replace: true});
    }

    const setPage = (page: number) => {
        const nextSearch = buildHotspotExplorerSearch(searchParams, {page});
        navigate(`${location.pathname}${nextSearch}`, {replace: true});
    }

    const openCounty = (county: string) => {
        navigate(`${buildCountyPath(countryCode, stateProvince, county)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`);
    }

    const openHotspot = (locationId: string) => {
        navigate(buildHotspotDetailPath(locationId), {
            state: {returnTo: `${location.pathname}${location.search}`}
        });
    }

    return (
        <HotspotExplorerShell
            backgroundImage={getHotspotExplorerHeroImage({routeLevel: 'state', regionBounds})}
            eyebrow="Hotspot Explorer"
            title={stateLabel}
            intro={`Hotspots you've visited in ${stateLabel}, ${countryLabel}.`}
            backLabel={`Back to ${countryLabel}`}
            onBack={() => navigate(`${buildCountryPath(countryCode)}${buildHotspotExplorerSearch(searchParams, {page: 1})}`)}
            stats={getCachedHotspotExplorerStats(visibleHotspots, 'Counties')}
            childStatLabel="Counties Visited"
            panelHeading="By County"
            groups={countyGroups}
            activeGroupCode={undefined}
            onSelectGroup={openCounty}
            hotspots={visibleHotspots}
            sortMode={sortMode}
            onChangeSort={setSortMode}
            currentPage={currentPage}
            onPageChange={setPage}
            onOpenHotspot={openHotspot}
        />
    );
}
